import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../components/app/Header";
import { api, FACILITY_META, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBookingGate } from "../lib/bookingGate";
import { toast } from "sonner";
import { showTagUsToast } from "../components/app/TagUsToast";
import ReportPriceButton from "../components/app/ReportPriceButton";
import VerifiedPricePill from "../components/app/VerifiedPricePill";
import ReportWebsiteButton from "../components/app/ReportWebsiteButton";
import LiveBrowsingTicker from "../components/app/LiveBrowsingTicker";
import {
  ShowerHead, Bath, Zap, Wifi, ShoppingBag, WashingMachine, Waves,
  Fish, Mountain, TrendingUp, Dog, Star, Heart, ArrowLeft, Send, Loader2,
  Calendar, MessageCircle, Plus, Sparkles, Pin, ThumbsUp, Lock,
} from "lucide-react";
import { MapContainer, TileLayer, Marker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import FindOnCampsitesCoUk from "../components/app/FindOnCampsitesCoUk";
import BookDirectOnSite from "../components/app/BookDirectOnSite";
import WeatherPanel from "../components/app/WeatherPanel";
import { useSeo } from "../lib/useSeo";
import NearbyAmenities from "../components/app/NearbyAmenities";
import { ProPackingList, ProDeals } from "../components/app/ProPerks";
import AffiliateKitBox from "../components/app/AffiliateKitBox";
import BookOnPitchup from "../components/app/BookOnPitchup";
import PhotoGallery from "../components/app/PhotoGallery";
import WallStripForCampsite from "../components/app/WallStripForCampsite";
import WalksNearbySection from "../components/app/WalksNearbySection";
import PromoCallout from "../components/app/PromoCallout";
import FisheriesNearbySection from "../components/app/FisheriesNearbySection";
import ShareMenu from "../components/app/ShareMenu";
import SubmitCampsitePhotoButton from "../components/app/SubmitCampsitePhotoButton";
import SubmitPhotoPrompt from "../components/app/SubmitPhotoPrompt";
import PriceVerifiedPill from "../components/app/PriceVerifiedPill";
import FavoritesActivityPill from "../components/app/FavoritesActivityPill";
import CampsiteSchema from "../components/app/CampsiteSchema";
import GooglePlacesPill from "../components/app/GooglePlacesPill";
import PartnerReelPin from "../components/app/PartnerReelPin";
import GoogleReviewsTab from "../components/app/GoogleReviewsTab";
import SignupNudgeModal from "../components/app/SignupNudgeModal";
import SaveCampsiteBar from "../components/app/SaveCampsiteBar";
import SaveThisChip from "../components/app/SaveThisChip";
import SignupPromptModal from "../components/app/SignupPromptModal";
import LockedCampsiteOverlay from "../components/app/LockedCampsiteOverlay";
const ICONS = { ShowerHead, Bath, Zap, Wifi, ShoppingBag, WashingMachine, Waves, Fish, Mountain, TrendingUp, Dog };

const markerIcon = L.divIcon({ className: "", html: `<div class="cc-marker">●</div>`, iconSize: [32, 32], iconAnchor: [16, 16] });

// iter122 — Fishery marker for nearby OSM-sourced day-ticket waters. Fish
// emoji on a cyan square so it reads clearly as secondary context next to
// the orange primary campsite pin.
const fisheryIcon = L.divIcon({
  className: "",
  html: `<div class="cc-fish-marker" aria-label="Fishery">🐟</div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});


// Replace Leaflet's "🇺🇦 Leaflet" default attribution prefix with a
// neutral "Map by Leaflet" so the Ukrainian-flag emoji doesn't get
// mistaken for "this map shows Ukraine" (iter52).
function DetailMapAttributionFix() {
  const map = useMap();
  React.useEffect(() => {
    try {
      map.attributionControl?.setPrefix('<a href="https://leafletjs.com/" target="_blank" rel="noopener">Map by Leaflet</a>');
    } catch { /* ignore */ }
  }, [map]);
  return null;
}


export default function CampsiteDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const gate = useBookingGate();
  const [site, setSite] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isFav, setIsFav] = useState(false);
  const [signupPromptOpen, setSignupPromptOpen] = useState(false);
  // Translated copy — populated when i18n language is not English. The
  // backend caches every (campsite, lang) pair so repeat views are
  // instant and Claude is only called the very first time.
  const [translation, setTranslation] = useState(null);
  const [tripStart, setTripStart] = useState("");
  const [tripEnd, setTripEnd] = useState("");
  const [showNewThread, setShowNewThread] = useState(false);
  const [ntTitle, setNtTitle] = useState("");
  const [ntBody, setNtBody] = useState("");
  const [ntBusy, setNtBusy] = useState(false);
  const [mapInteractive, setMapInteractive] = useState(false);
  // iter122 — Fisheries within 10 km of this campsite, used to drop fish
  // pins on the detail-page Leaflet map (in addition to the listing
  // section). Fetched independently of the listing component so a fast
  // map render never has to wait for the section to mount.
  const [mapFisheries, setMapFisheries] = useState([]);
  const [reviewSummary, setReviewSummary] = useState(null);
  const [summarising, setSummarising] = useState(false);
  const { user } = useAuth();
  // iter217 — the top ~300 featured campsites are fully viewable for
  // anonymous visitors (prices, map, description, booking). The rest
  // remain gated. This flag comes from the backend on each detail fetch
  // via `site.unlocked_for_anon` — we hoist it into a local so the
  // JSX stays readable ("canSeePremium" reads better than a repeated
  // negated ternary spread across three lock blocks).
  const canSeePremium = !!user || !!site?.unlocked_for_anon;
  const navigate = useNavigate();

  // iter123 — Per-listing SEO meta + Schema.org Campground markup.
  // Site is null on first paint; useSeo only injects once site loads.
  // Schema.org rich snippets unlock Google star ratings, location pin,
  // and price band right in search results — biggest free CTR win.
  const seoJsonLd = site ? {
    "@context": "https://schema.org",
    "@type": "Campground",
    "name": site.name,
    "description": (site.description || "").slice(0, 500),
    "image": site.image || site.images?.[0] || undefined,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": site.region,
      "addressCountry": site.country || "United Kingdom",
      "streetAddress": site.address || undefined,
    },
    "geo": (site.lat != null && site.lng != null) ? {
      "@type": "GeoCoordinates",
      "latitude": site.lat,
      "longitude": site.lng,
    } : undefined,
    "url": `https://camp-compass.uk/campsites/${site.campsite_id}`,
    "telephone": site.phone || undefined,
    "priceRange": site.price_per_night ? `£${Math.floor(site.price_per_night)}` : "££",
    "aggregateRating": (site.rating && site.review_count) ? {
      "@type": "AggregateRating",
      "ratingValue": site.rating,
      "reviewCount": site.review_count,
      "bestRating": 5,
      "worstRating": 1,
    } : undefined,
    "amenityFeature": (site.facilities || []).slice(0, 12).map((f) => ({
      "@type": "LocationFeatureSpecification",
      "name": f,
      "value": true,
    })),
  } : null;

  const seoTitle = site
    ? `${site.name}${site.region ? ` · ${site.region}` : ""} — CampCompass UK`
    : null;
  const seoDescription = site
    ? `${site.name}${site.region ? ` in ${site.region}` : ""}. ${(site.description || "").replace(/<[^>]+>/g, "").slice(0, 155)}`.trim()
    : null;

  useSeo({
    title: seoTitle,
    description: seoDescription,
    canonical: site ? `/campsites/${site.campsite_id}` : null,
    ogImage: site?.image || site?.images?.[0],
    jsonLd: seoJsonLd,
    jsonLdId: "campsite",
  });

  useEffect(() => {
    (async () => {
      // Fire the three core fetches independently so a failure in
      // reviews or community threads doesn't blank out the whole page.
      const [siteRes, reviewsRes, threadsRes] = await Promise.allSettled([
        api.get(`/campsites/${id}`),
        api.get(`/reviews/${id}`),
        api.get(`/community/threads?campsite_id=${id}`),
      ]);
      if (siteRes.status === "fulfilled") {
        setSite(siteRes.value.data);
      } else {
        // Only show a toast when the primary campsite payload fails —
        // a missing reviews/threads list shouldn't bother the user.
        toast.error(formatErr(siteRes.reason));
      }
      if (reviewsRes.status === "fulfilled") setReviews(reviewsRes.value.data || []);
      if (threadsRes.status === "fulfilled") setThreads(threadsRes.value.data || []);
      setLoading(false);
      // iter121 — fire-and-forget visit tracking. Hourly-deduped server-side,
      // so refresh-spam can't inflate counts. Never blocks the render.
      api.post(`/campsites/${id}/view`).catch(() => {});
    })();
  }, [id]);

  // iter122 — Fetch nearby fisheries so we can render fish pins on the map.
  // Separate from the FisheriesNearbySection's own fetch (which uses the same
  // endpoint with a server-side cache so a duplicate call is essentially
  // free). Fire-and-forget — failures simply leave the map without pins.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/campsites/${id}/fisheries`);
        if (!cancelled) setMapFisheries(data?.fisheries || []);
      } catch {
        if (!cancelled) setMapFisheries([]);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // Fetch translated name + description whenever the user switches to a
  // non-English language. Skips the call (and the toast/spinner) when the
  // language is English so the page stays instant for UK visitors.
  useEffect(() => {
    const lang = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
    if (!id || lang === "en") {
      setTranslation(null);
      return;
    }
    let alive = true;
    api.get(`/campsites/${id}/translate?lang=${encodeURIComponent(lang)}`)
      .then(({ data }) => {
        if (alive && data?.description) setTranslation(data);
      })
      .catch(() => { if (alive) setTranslation(null); });
    return () => { alive = false; };
  }, [id, i18n.resolvedLanguage, i18n.language]);

  // iter94 — lazy enrichment for thin descriptions. Many sites have very
  // basic descriptions ("Family campsite with sea views.") which makes
  // the detail page look threadbare. On first view of a thin site, we
  // ask Claude to write a richer 2-3 paragraph description and cache it
  // forever. Subsequent viewers (and translations) read from the cache.
  // Skipped silently if a richer description is already present.
  const [enriching, setEnriching] = useState(false);
  useEffect(() => {
    if (!site || !id) return;
    if (site.description_source === "ai_enriched") return; // already rich
    if ((site.description || "").length >= 220) return;     // long enough
    let alive = true;
    setEnriching(true);
    api.post(`/campsites/${id}/enrich`)
      .then(({ data }) => {
        if (alive && data?.description) {
          setSite((prev) => prev ? { ...prev, description: data.description, description_source: "ai_enriched" } : prev);
        }
      })
      .catch(() => {})
      .finally(() => { if (alive) setEnriching(false); });
    return () => { alive = false; };
  }, [site, id]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/favorites");
        const mine = data.find((d) => d.campsite_id === id);
        setIsFav(!!mine);
        if (mine) {
          setTripStart(mine.trip_start?.slice(0, 10) || "");
          setTripEnd(mine.trip_end?.slice(0, 10) || "");
        }
      } catch {}
    })();
  }, [user, id]);

  const REVIEW_MIN = 10;
  const REVIEW_MAX = 2000;

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { toast.info("Log in to review"); return; }
    const trimmed = comment.trim();
    if (trimmed.length < REVIEW_MIN) {
      toast.info(`Reviews need at least ${REVIEW_MIN} characters — tell us a bit more so it actually helps`);
      return;
    }
    if (trimmed.length > REVIEW_MAX) {
      toast.error(`Reviews must be under ${REVIEW_MAX} characters`);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/reviews", { campsite_id: id, rating, comment });
      setReviews([data, ...reviews]);
      const sharedRating = rating;
      const sharedName = site?.name || "this site";
      const trimmedLen = comment.trim().length;
      setComment("");
      setRating(5);
      // Long reviews get a celebratory toast that explicitly thanks the
      // user for their detail — reinforces the behaviour we want.
      if (trimmedLen >= 200) {
        toast.success(
          `Your ${Math.round(trimmedLen / 100) * 100}-character review will really help future campers ❤️`,
          { duration: 4500, icon: "🏕️" },
        );
      }
      // Replace plain success toast with a "tag us!" prompt — turns every
      // posted review into a chance for organic social promotion.
      showTagUsToast({
        headline: trimmedLen >= 200 ? "Detailed review posted — legend!" : "Review posted — thank you!",
        captionInput: { kind: "review", name: sharedName, rating: sharedRating },
      });
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSubmitting(false); }
  };

  const toggleHelpful = async (reviewId) => {
    if (!user) { toast.info("Log in to vote 'Helpful'"); return; }
    try {
      const { data } = await api.post(`/reviews/${reviewId}/helpful`);
      setReviews((prev) => prev.map((r) =>
        r.review_id === reviewId
          ? { ...r, helpful_count: data.helpful_count, _marked_helpful: data.marked_helpful }
          : r,
      ));
      if (data.marked_helpful) {
        toast.success("Marked helpful — thanks for ranking the best!");
      }
    } catch (e) { toast.error(formatErr(e)); }
  };

  const toggleFav = async () => {
    if (!user) {
      // iter176 — replace the dead "log in" toast with an inline signup
      // prompt that carries a returnTo so the user lands back here after
      // creating an account. Turns "intent to save" into signups.
      setSignupPromptOpen(true);
      return;
    }
    try {
      if (isFav) { await api.delete(`/favorites/${id}`); setIsFav(false); setTripStart(""); setTripEnd(""); }
      else { await api.post(`/favorites/${id}`); setIsFav(true); }
    } catch (e) { toast.error(formatErr(e)); }
  };

  const saveDates = async () => {
    if (!user || !isFav) { toast.info("Save this campsite first, then add trip dates"); return; }
    try {
      await api.patch(`/favorites/${id}`, { trip_start: tripStart || null, trip_end: tripEnd || null });
      const tripDateLabel = tripStart && tripEnd
        ? `${new Date(tripStart).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} → ${new Date(tripEnd).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`
        : "";
      toast.success("Trip dates saved — we'll ping you 48h before");
      // Trip-saved nudge: ask the user to share their plans with a tag.
      showTagUsToast({
        headline: `Trip booked! ${site?.name || ""} 🏕️`,
        captionInput: { kind: "trip", name: site?.name || "a UK campsite", date: tripDateLabel },
      });
    } catch (e) { toast.error(formatErr(e)); }
  };

  const postNewThread = async (e) => {
    e.preventDefault();
    if (!user) { navigate("/login"); return; }
    if (ntTitle.trim().length < 3 || ntBody.trim().length < 5) {
      toast.info("Title 3+ chars, body 5+ chars"); return;
    }
    setNtBusy(true);
    try {
      const { data } = await api.post("/community/threads", {
        title: ntTitle, body: ntBody, tag: "Recommendations", campsite_id: id,
      });
      toast.success("Thread posted");
      setThreads([data, ...threads]);
      setNtTitle(""); setNtBody(""); setShowNewThread(false);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setNtBusy(false); }
  };

  const getReviewSummary = async () => {
    setSummarising(true);
    try {
      const { data } = await api.post(`/reviews/${id}/summary`);
      setReviewSummary(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSummarising(false); }
  };

  const askCamper = (question) => {
    if (!site) return;
    const text = `About ${site.name} (${site.region}): ${question}`;
    window.dispatchEvent(new CustomEvent("cc:chat:ask", { detail: { text } }));
  };

  if (loading) return (
    <div className="min-h-screen"><Header /><div className="p-20 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div></div>
  );
  if (!site) return <div className="min-h-screen"><Header /><div className="p-20">Not found</div></div>;

  const avg = reviews.length ? reviews.reduce((a, r) => a + r.rating, 0) / reviews.length : site.rating;

  // Hero photo: prefer first user/admin image, fallback to legacy `site.image`
  const _heroImg = (site.images && site.images.length > 0)
    ? (typeof site.images[0] === "string" ? site.images[0] : site.images[0]?.url)
    : site.image;

  return (
    <div className="min-h-screen bg-[#fafafa] overflow-x-hidden" data-testid="detail-page">
      <CampsiteSchema site={site} campsiteId={id} reviews={reviews} avgRating={typeof avg === "number" ? avg : null} />
      <Header />

      {/* iter125 — Full-bleed hero with title overlay + floating action card.
          Photo bleeds edge-to-edge on mobile; constrained on desktop. */}
      <div className="relative w-full" data-testid="detail-hero">
        <div className="relative w-full h-[280px] sm:h-[360px] lg:h-[460px] overflow-hidden bg-[#0e0e0e]">
          {_heroImg ? (
            <img src={_heroImg} alt={site.name} className="absolute inset-0 w-full h-full object-cover" />
          ) : null}
          {/* Dark gradient bottom-up so title stays legible against any photo */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10 pointer-events-none" />
          {/* Floating back chip — soft pill, blends into the hero */}
          <Link
            to="/browse"
            className="absolute top-3 left-3 sm:top-5 sm:left-5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/85 backdrop-blur-md text-[#444] border border-[#d4d4d4] mono text-[10px] uppercase tracking-wider hover:bg-white hover:text-[#ff4500] hover:border-[#ff4500] transition-colors shadow-sm"
            data-testid="back-link"
          >
            <ArrowLeft className="w-3 h-3" /> Back
          </Link>
          {/* Title block over hero */}
          <div className="absolute bottom-0 left-0 right-0 px-5 sm:px-8 lg:px-12 pb-8 sm:pb-10 text-white">
            <div className="mono text-[10px] sm:text-xs uppercase tracking-[0.18em] text-[#fde047] mb-2 drop-shadow" data-testid="detail-region">
              {site.region}
            </div>
            <h1
              className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-bold drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)] break-words max-w-[820px]"
              data-testid="detail-title"
            >
              {translation?.name || site.name}
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">

        {/* Floating action card — overlaps the hero by ~40px (mobile) / 56px (desktop) */}
        <div
          className="relative -mt-10 sm:-mt-14 mb-8 z-10 bg-white rounded-2xl border border-[#e5e5e5] shadow-[0_10px_30px_-8px_rgba(0,0,0,0.18)]"
          data-testid="detail-action-card"
        >
          <div className="p-4 sm:p-6 flex items-start gap-4">
            {/* Left — rating + price */}
            <div className="flex-1 min-w-0 space-y-2">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#fff4ee] border border-[#ffd9c2]">
                <Star className="w-4 h-4 sm:w-5 sm:h-5 fill-[#ff4500] text-[#ff4500] shrink-0" strokeWidth={1.5} />
                <span className="font-display text-lg sm:text-xl font-bold leading-none text-[#0e0e0e]">
                  {typeof avg === "number" ? avg.toFixed(1) : "—"}
                </span>
                <span className="mono text-[11px] sm:text-xs text-[#ff4500] font-bold">({reviews.length})</span>
              </div>
              {canSeePremium ? (
                <>
                  <div className="mono text-[11px] sm:text-xs uppercase tracking-wider text-[#444]">
                    {typeof site.price_per_night === "number"
                      ? <>From <span className="text-[#0e0e0e] font-bold">£{site.price_per_night.toFixed(2)}</span>/night</>
                      : <span>Price on Pitchup</span>}
                  </div>
                  <PriceVerifiedPill site={site} className="mt-0.5" />
                  <FavoritesActivityPill campsiteId={id} bump={isFav ? 1 : 0} className="mt-0.5" />
                  {site?.price_verified_at && site?.price_verified_count && (
                    <ReportPriceButton campsiteId={id} currentPrice={typeof site.price_per_night === "number" ? site.price_per_night : null} variant="help" />
                  )}
                  {typeof site.lat === "number" && typeof site.lng === "number" && (
                    <div className="inline-block mono text-[10px] sm:text-[11px] text-[#666] tracking-wide pt-1">
                      {site.lat.toFixed(3)}°N, {Math.abs(site.lng).toFixed(3)}°{site.lng < 0 ? "W" : "E"}
                    </div>
                  )}
                </>
              ) : (
                <a
                  href={`/register?next=${encodeURIComponent(typeof window !== "undefined" ? window.location.pathname : `/campsites/${id}`)}`}
                  className="inline-flex items-center gap-1.5 bg-[#fff4ec] border border-[#ff4500]/50 text-[#7a2000] mono text-[10px] sm:text-[11px] uppercase tracking-widest px-2.5 py-1 hover:bg-[#ffe4d3] transition-colors"
                  data-testid="detail-action-price-locked"
                  title="Sign up free to see this campsite's live price"
                >
                  <Lock className="w-3 h-3" /> Sign up free to see price
                </a>
              )}
            </div>
            {/* Right — stacked orange action buttons (same colour as elsewhere in app) */}
            <div className="flex flex-col gap-2 shrink-0 w-[140px] sm:w-[180px]">
              <ShareMenu
                url={`${process.env.REACT_APP_BACKEND_URL}/api/share/campsite/${id}`}
                title={`${site.name} · CampCompass UK`}
                text={`Found ${site.name} in ${site.region} on CampCompass UK — ${site.description ? site.description.slice(0, 140) : "honest UK campsite comparisons"}`}
                imageDataUrl={null}
                buttonClassName="w-full inline-flex items-center justify-center gap-1.5 bg-[#ff4500] hover:bg-[#e63e00] text-white border border-[#ff4500] rounded-xl px-3 py-2.5 text-[11px] sm:text-xs mono uppercase tracking-wider transition-colors shadow-sm"
                buttonLabel="Share"
                testId="detail-share"
              />
              <SubmitCampsitePhotoButton
                campsiteId={id}
                campsiteName={site.name}
                className="!w-full !justify-center !bg-[#ff4500] !text-white !border !border-[#ff4500] !rounded-xl hover:!bg-[#e63e00] hover:!border-[#e63e00] hover:!text-white !px-3 !py-2.5 text-[11px] sm:text-xs !shadow-sm"
              />
            </div>
          </div>
          {/* Photo strip — sits as a separate band inside the card */}
          <div className="border-t border-[#e5e5e5] p-3 sm:p-4 bg-[#fffbf2] rounded-b-2xl">
            <PhotoGallery
              campsiteId={id}
              images={site.images && site.images.length > 0 ? site.images : (site.image ? [{ url: site.image, source: "stock" }] : [])}
              onPhotoAdded={() => api.get(`/campsites/${id}`).then(r => setSite(r.data))}
            />
          </div>
        </div>

        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10">
          <div className="min-w-0">
            <PartnerReelPin campsiteId={id} />
            <div className="mb-6">
              <LiveBrowsingTicker siteId={id} />
              <GooglePlacesPill campsiteId={id} />
            </div>

            <div className="text-lg leading-relaxed text-[#333] mb-3 max-w-2xl" data-testid="detail-description">
              {(() => {
                const fullText = (translation?.description || site.description || "");
                // iter194 — water-tight anon gate: guests see the first
                // ~180 characters only, blurred tail, with a signup CTA
                // to read the rest. Signed-in members see everything.
                if (!canSeePremium && fullText.length > 180) {
                  const preview = fullText.slice(0, 180).trimEnd();
                  return (
                    <>
                      <p>{preview}…</p>
                      <div
                        className="mt-3 inline-flex flex-col sm:flex-row items-start sm:items-center gap-2 bg-[#fff4ee] border-2 border-[#ff4500]/40 px-4 py-3"
                        data-testid="detail-description-anon-lock"
                      >
                        <span className="text-[13px] text-[#7a2000] leading-snug">
                          <strong>Sign up free</strong> to read the full write-up, prices, map, and everything else.
                        </span>
                        <a
                          href={`/register?next=${encodeURIComponent(window.location.pathname)}`}
                          className="inline-flex items-center gap-1 bg-[#0a7f3f] text-white text-[11px] font-black uppercase tracking-widest px-3 py-1.5 shadow-[0_2px_0_0_#111] hover:translate-y-[1px] hover:shadow-[0_1px_0_0_#111] transition-transform"
                          data-testid="detail-description-anon-signup"
                        >
                          Unlock full page
                        </a>
                      </div>
                    </>
                  );
                }
                return fullText.split(/\n{2,}/).map((para, i) => (
                  <p key={i} className={i > 0 ? "mt-3" : ""}>{para}</p>
                ));
              })()}
              {enriching ? (
                <p className="mt-3 mono text-[11px] uppercase tracking-[0.2em] text-[#888] inline-flex items-center gap-1.5">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#ff4500] animate-pulse" />
                  Writing a richer description…
                </p>
              ) : null}
            </div>
            {user && user.role === "admin" ? (
              <div className="mb-10 max-w-2xl">
                <button
                  type="button"
                  onClick={async () => {
                    if (!window.confirm("Re-generate this description? The current one will be overwritten.")) return;
                    setEnriching(true);
                    try {
                      const { data } = await api.post(`/campsites/${id}/enrich?force=true`);
                      if (data?.description) {
                        setSite((prev) => prev ? { ...prev, description: data.description, description_source: "ai_enriched" } : prev);
                        toast.success("Description refreshed");
                      }
                    } catch (e) {
                      toast.error(formatErr(e));
                    } finally {
                      setEnriching(false);
                    }
                  }}
                  disabled={enriching}
                  className="text-[11px] mono uppercase tracking-[0.15em] px-3 py-1.5 border-2 border-[#1c1917] bg-white hover:bg-[#fde047] transition-colors disabled:opacity-50"
                  data-testid="admin-refresh-description"
                >
                  ✨ Refresh description (admin)
                </button>
                <span className="ml-2 mono text-[10px] text-[#888]">
                  Source: {site.description_source || "original"}
                </span>
              </div>
            ) : <div className="mb-10" />}

            <SubmitPhotoPrompt
              campsiteId={id}
              campsiteName={site.name}
              images={site.images || []}
            />

            {/* Ask Camper shortcut chips */}
            <div className="mb-10 p-5 border hairline bg-[#fffdfa]" data-testid="ask-chips">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-[#ff4500]" strokeWidth={1.5} />
                <div className="eyebrow">{t("campsite.askCamper")}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  "Is this good for dogs?",
                  "How busy is it in August?",
                  "What's the weather usually like?",
                  "Is it beginner-friendly?",
                  "Any nearby pubs or shops?",
                ].map((q) => (
                  <button key={q}
                    onClick={() => askCamper(q)}
                    className="px-3 py-1.5 text-xs border hairline bg-white hover:border-[#ff4500] hover:text-[#ff4500] transition-colors"
                    data-testid={`chip-${q.slice(0,6)}`}>
                    {q}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-10">
              <div className="eyebrow mb-4">Weather Outlook</div>
              <WeatherPanel lat={site.lat} lng={site.lng} days={user?.is_pro ? 14 : 7} />
            </div>

            {/* iter163 — Newspaper promo callout (National Trust / RSPB /
                English Heritage / Kew / Wildlife Trusts family passes).
                Self-hides when no active promo matches this campsite's
                nearby operators. */}
            <PromoCallout campsiteId={id} />

            {/* iter108 — Local Walks Nearby. Placed right under weather so
                it's the first thing trip-planners see when they land on a
                detail page — never scrolled-to. */}
            <WalksNearbySection campsiteId={id} campsiteName={site.name} />
            <FisheriesNearbySection campsiteId={id} campsiteName={site.name} />

            {/* Trip Wall — moved up (iter98) so visitors see real-camper
                stories without having to scroll past facilities + weather.
                Real social proof drives more clicks than feature lists. */}
            <div className="mb-10">
              <WallStripForCampsite campsiteId={id} campsiteName={site.name} />
            </div>

            <ProPackingList campsiteId={id} />
            <AffiliateKitBox />
            <ProDeals />

            <div className="mb-10">
              <div className="eyebrow mb-4">{t("campsite.facilities")}</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {site.facilities.map((f) => {
                  const meta = FACILITY_META[f]; const I = meta ? ICONS[meta.icon] : null;
                  return (
                    <div key={f} className="flex items-center gap-2 p-3 border hairline bg-white" data-testid={`fac-${f}`}>
                      {I && <I className="w-4 h-4 text-[#ff4500]" strokeWidth={1.5} />}
                      <span className="text-sm">{meta?.label || f}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <NearbyAmenities campsiteId={id} />

            <GoogleReviewsTab campsiteId={id} />

            {/* Threads about this campsite */}
            <div className="mb-10">
              <div className="flex items-center justify-between mb-4">
                <div className="eyebrow flex items-center gap-2"><MessageCircle className="w-3 h-3" /> {t("campsite.threadsTitle")}</div>
                <button
                  onClick={() => user ? setShowNewThread(true) : navigate("/login")}
                  className="btn-secondary !px-3 !py-2 text-[11px] flex items-center gap-1.5"
                  data-testid="start-thread-btn">
                  <Plus className="w-3 h-3" /> {t("campsite.startThread")}
                </button>
              </div>

              {showNewThread && (
                <form onSubmit={postNewThread} className="p-5 border hairline bg-white mb-4" data-testid="site-thread-form">
                  <div className="eyebrow mb-2">{t("campsite.newThreadEyebrow")}</div>
                  <input className="cc-input mb-2" placeholder={t("campsite.threadTitlePlaceholder", { name: site.name })}
                    value={ntTitle} onChange={(e) => setNtTitle(e.target.value)} data-testid="site-thread-title" />
                  <textarea className="cc-input h-24" placeholder={t("campsite.threadBodyPlaceholder")}
                    value={ntBody} onChange={(e) => setNtBody(e.target.value)} data-testid="site-thread-body" />
                  <div className="flex gap-2 mt-3">
                    <button className="btn-primary !px-4 !py-2 text-xs" disabled={ntBusy} data-testid="site-thread-submit">
                      {ntBusy ? t("campsite.threadPosting") : t("campsite.threadPostBtn")}
                    </button>
                    <button type="button" onClick={() => setShowNewThread(false)} className="btn-secondary !px-4 !py-2 text-xs">{t("campsite.cancel")}</button>
                  </div>
                </form>
              )}

              {threads.length === 0 ? (
                <div className="p-5 border hairline bg-white text-sm text-[#888]" data-testid="no-threads">
                  {t("campsite.noThreadsYet")}
                </div>
              ) : (
                <div className="space-y-3" data-testid="site-threads">
                  {threads.map((t, i) => (
                    <Link key={t.thread_id} to={`/community/${t.thread_id}`}
                      className="block p-4 border hairline bg-white hover:border-[#ff4500] transition-colors"
                      data-testid={`site-thread-${i}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider border hairline mono">{t.tag}</span>
                        {t.author_badge && <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider bg-[#ffefea] text-[#ff4500]">{t.author_badge}</span>}
                      </div>
                      <div className="font-display text-base">{t.title}</div>
                      <div className="flex items-center gap-4 mt-1 text-xs mono text-[#888]">
                        <span>{t.user_name}</span>
                        <span>·</span>
                        <span><MessageCircle className="w-3 h-3 inline mr-1" />{t.reply_count}</span>
                        <span><Heart className="w-3 h-3 inline mr-1" />{t.kudos || 0}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Reviews */}
            <div className="mb-10">
              <div className="flex items-center justify-between mb-4">
                <div className="eyebrow">{t("campsite.fieldNotes", { count: reviews.length })}</div>
                {reviews.length >= 2 && (
                  <button onClick={getReviewSummary} disabled={summarising}
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-1.5 border hairline bg-white hover:border-[#ff4500] hover:text-[#ff4500]"
                    data-testid="ai-review-summary">
                    {summarising ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    {t("campsite.aiSummary")}
                  </button>
                )}
              </div>

              {reviewSummary && (
                <div className="p-5 border hairline bg-[#fffdfa] mb-6" data-testid="review-summary-box">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-[#ff4500]" strokeWidth={1.5} />
                    <div className="eyebrow">{t("campsite.aiDistilled", { count: reviewSummary.review_count })}</div>
                    {reviewSummary.cached && <span className="text-[10px] mono text-[#888]">{t("campsite.cached")}</span>}
                  </div>
                  <p className="text-sm text-[#222] leading-relaxed whitespace-pre-wrap">{reviewSummary.summary}</p>
                </div>
              )}

              <form onSubmit={submitReview} className="p-5 border hairline bg-white mb-6" data-testid="review-form">
                <div className="flex items-center gap-3 mb-3">
                  <div className="eyebrow">{t("campsite.yourRating")}</div>
                  {[1,2,3,4,5].map((n) => (
                    <button type="button" key={n} onClick={() => setRating(n)} data-testid={`rate-${n}`}>
                      <Star className={`w-5 h-5 ${n <= rating ? "fill-[#ff4500] text-[#ff4500]" : "text-[#ccc]"}`} strokeWidth={1.5} />
                    </button>
                  ))}
                </div>
                <div>
                  <textarea
                    className="cc-input h-40 sm:h-48 resize-y"
                    placeholder="What was the site really like? Showers · pitches · staff · vibe · the bit you'll remember in 6 months. Tip: future campers REALLY appreciate honest detail."
                    value={comment}
                    onChange={(e) => setComment(e.target.value.slice(0, REVIEW_MAX))}
                    maxLength={REVIEW_MAX}
                    minLength={REVIEW_MIN}
                    data-testid="review-comment"
                  />
                  <div className="flex items-center justify-between mt-1 text-[11px] mono">
                    <span className={comment.trim().length > 0 && comment.trim().length < REVIEW_MIN ? "text-amber-600" : "text-[#888]"}>
                      {comment.trim().length < REVIEW_MIN
                        ? `${REVIEW_MIN - comment.trim().length} more characters to post`
                        : "✓ ready to post"}
                    </span>
                    <span className={comment.length > REVIEW_MAX - 100 ? "text-amber-600 font-bold" : "text-[#888]"} data-testid="review-char-count">
                      {comment.length} / {REVIEW_MAX}
                    </span>
                  </div>
                </div>
                <button className="btn-primary mt-3 inline-flex items-center gap-2" disabled={submitting} data-testid="review-submit">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {t("campsite.postReview")}
                </button>
              </form>

              <div className="space-y-4">
                {reviews.map((r, i) => {
                  const helpfulCount = r.helpful_count || 0;
                  // Top-pinned review = first item AND has at least 1 helpful vote
                  const isPinned = i === 0 && helpfulCount >= 1;
                  const marked = r._marked_helpful;
                  return (
                    <div
                      key={r.review_id}
                      className={`p-5 border bg-white ${isPinned ? "border-2 border-[#ff4500] shadow-[4px_4px_0_0_#111]" : "hairline"}`}
                      data-testid={`review-${i}`}
                    >
                      {isPinned && (
                        <div className="inline-flex items-center gap-1.5 mb-3 px-2 py-1 bg-[#ff4500] text-white text-[10px] mono uppercase tracking-[0.2em]" data-testid="review-pinned-badge">
                          <Pin className="w-3 h-3" /> Most helpful
                        </div>
                      )}
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-display text-lg">{r.user_name}</div>
                        <div className="flex">
                          {[1,2,3,4,5].map((n) => (
                            <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "fill-[#ff4500] text-[#ff4500]" : "text-[#ddd]"}`} strokeWidth={1.5} />
                          ))}
                        </div>
                      </div>
                      <p className="text-sm text-[#333] whitespace-pre-wrap leading-relaxed">{r.comment}</p>
                      <div className="flex items-center justify-between mt-3 gap-3">
                        <div className="mono text-[11px] text-[#888]">{new Date(r.created_at).toLocaleDateString()}</div>
                        {user && r.user_id !== user.user_id && (
                          <button
                            type="button"
                            onClick={() => toggleHelpful(r.review_id)}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 border text-[11px] mono uppercase tracking-[0.18em] transition-colors ${marked ? "bg-emerald-50 border-emerald-500 text-emerald-700" : "hairline hover:border-[#ff4500] hover:text-[#ff4500]"}`}
                            data-testid={`review-helpful-${i}`}
                            aria-pressed={marked ? "true" : "false"}
                          >
                            <ThumbsUp className="w-3 h-3" />
                            {marked ? "Marked helpful" : "Helpful"}
                            {helpfulCount > 0 && <span className="font-bold">· {helpfulCount}</span>}
                          </button>
                        )}
                        {(!user || r.user_id === user.user_id) && helpfulCount > 0 && (
                          <span className="inline-flex items-center gap-1 text-[11px] mono text-[#888]" data-testid={`review-helpful-readonly-${i}`}>
                            <ThumbsUp className="w-3 h-3" /> {helpfulCount} found this helpful
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {!reviews.length && <div className="text-sm text-[#888]">{t("campsite.noReviewsYet")}</div>}
              </div>
            </div>
          </div>

          {/* Side */}
          <aside className="lg:sticky lg:top-24 self-start min-w-0">
            {!user && site?.unlocked_for_anon && (
              <div
                className="mb-3 border-2 border-[#ff4500] bg-[#fff8f4] px-3 py-2 inline-flex items-center gap-2 text-[11px] mono uppercase tracking-widest"
                data-testid="detail-featured-public-pill"
                title="One of our featured campsites — freely viewable without an account. Sign up to save it, message the owner, or leave a review."
              >
                <span className="text-[#ff4500]">★</span>
                <span className="text-[#7a2000]">Featured · freely viewable</span>
              </div>
            )}
            {canSeePremium ? (
            <div className="border hairline bg-white p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="eyebrow">From</div>
                <button onClick={toggleFav} className="flex items-center gap-1.5 text-xs uppercase tracking-wider" data-testid="detail-fav-btn">
                  <Heart className={`w-4 h-4 ${isFav ? "fill-[#ff4500] text-[#ff4500]" : ""}`} strokeWidth={1.5} />
                  {isFav ? t("campsite.saved") : t("campsite.save")}
                </button>
              </div>
              <div className="font-display text-4xl mono">
                {typeof site.price_per_night === "number" ? (
                  <>£{site.price_per_night.toFixed(2)}<span className="text-lg text-[#888]"> / night</span></>
                ) : (
                  <span className="text-2xl text-[#666]">{t("campsite.seeOnPitchup")}</span>
                )}
              </div>
              <div className="flex items-center justify-between gap-2 mt-1">
                <div className="text-[11px] text-[#888]">Starting price · live rates on Pitchup</div>
                <ReportPriceButton campsiteId={id} currentPrice={typeof site.price_per_night === "number" ? site.price_per_night : null} />
              </div>
              <VerifiedPricePill verifiedAt={site.price_verified_at} verifiedCount={site.price_verified_count} />
              {!site.price_verified_at && typeof site.price_per_night === "number" && (
                <div
                  className="inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 bg-[#fff7ed] border border-[#fed7aa] text-[11px] mono text-[#9a3412]"
                  title="Help fellow campers — verify or correct this price"
                  data-testid="unverified-price-pill"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#ff4500]" />
                  <span>Be the first to verify this price</span>
                </div>
              )}
              <div className="mt-6 space-y-2 text-sm">
                <Row k="Tent" v={site.tent ? "Yes" : "No"} />
                <Row k="Caravan" v={site.caravan ? "Yes" : "No"} />
                <Row k="Campervan" v={site.campervan ? "Yes" : "No"} />
                <Row k="Trailer tent" v={
                  site.trailer_tent === false ? "No"
                    : (site.trailer_tent || (site.tent && (site.caravan || site.campervan)))
                      ? "Yes" : "—"
                } />
                <Row k="Camping pod" v={
                  site.camping_pod === false ? "No"
                    : (site.camping_pod
                       || /pod|glamp/i.test(site.name || "")
                       || /camping pod|glamping pod|wooden pod/i.test(site.description || ""))
                      ? "Yes" : "—"
                } />
                <Row k="Rating" v={typeof site.rating === "number" ? `${site.rating.toFixed(1)} / 5` : "Not yet rated"} />
              </div>

              {isFav && (
                <div className="mt-6 p-3 bg-[#fafafa] border hairline" data-testid="detail-trip-dates">
                  <div className="eyebrow mb-2 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Your Trip Dates</div>
                  <div className="flex gap-2">
                    <input type="date" className="cc-input !p-1.5 text-xs" value={tripStart}
                      onChange={(e) => setTripStart(e.target.value)} data-testid="detail-trip-start" />
                    <input type="date" className="cc-input !p-1.5 text-xs" value={tripEnd}
                      onChange={(e) => setTripEnd(e.target.value)} data-testid="detail-trip-end" />
                  </div>
                  <button onClick={saveDates} className="mt-2 text-[11px] mono underline text-[#ff4500]" data-testid="detail-save-dates">
                    Save · 48h weather alert
                  </button>
                </div>
              )}
            </div>
            ) : (
              /* iter189 — anonymous visitors see a blurred, locked version
                 of the price + facilities card with a sign-up CTA on top. */
              <div className="mb-6" data-testid="detail-price-locked">
                <LockedCampsiteOverlay label="Prices & facilities">
                  <div className="border hairline bg-white p-6">
                    <div className="eyebrow mb-3">From</div>
                    <div className="font-display text-4xl mono">£••.••<span className="text-lg text-[#888]"> / night</span></div>
                    <div className="mt-6 space-y-2 text-sm">
                      <Row k="Tent" v="•••" />
                      <Row k="Caravan" v="•••" />
                      <Row k="Campervan" v="•••" />
                      <Row k="Rating" v="•••" />
                    </div>
                  </div>
                </LockedCampsiteOverlay>
              </div>
            )}

            {canSeePremium && (
              <>
              {/* Booking options — equal-weight grid: Pitchup (orange) ·
                  Campsites.co.uk (black) · Site website (blue) · Phone (green).
                  Signed-in only. */}
              <div className="mt-6 grid sm:grid-cols-2 gap-3 mb-6" data-testid="dual-booking">
                <BookOnPitchup
                  campsiteId={id}
                  pitchupListed={site.pitchup_listed}
                  pitchupUrl={site.pitchup_url || null}
                  pitchupUrlOk={site.pitchup_url_ok}
                  campsiteName={site.name}
                  campsiteRegion={site.region}
                  websiteUrl={site.website || null}
                  phone={site.phone || null}
                  googleMapsUrl={site.google_maps_url || null}
                  tripStart={tripStart || null}
                  tripEnd={tripEnd || null}
                  variant="primary"
                  size="lg"
                  label="Book on Pitchup"
                  testId="book-affiliate"
                  fullWidth
                  hideWebsiteFallback
                />
                {site.campsites_co_uk_url && (
                  <FindOnCampsitesCoUk
                    url={site.campsites_co_uk_url}
                    campsiteName={site.name}
                    testId="book-campsites-co-uk"
                  />
                )}
                <BookDirectOnSite
                  websiteUrl={site.website || null}
                  campsiteName={site.name}
                  campsiteRegion={site.region}
                  testId="book-direct-site"
                />
                {site.phone && (
                  <a
                    href={`tel:${String(site.phone).replace(/[^+\d]/g, "")}`}
                    onClick={(e) => gate(e, { reason: "call this campsite" })}
                    className="inline-flex items-center justify-center w-full px-4 py-4 bg-[#1f7a35] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#196a2d] active:translate-y-[1px] transition-colors gap-2 shadow-[0_2px_0_0_#0e4a1e]"
                    data-testid="book-call-direct"
                    title={`Call ${site.name} direct`}
                  >
                    Call site direct
                    <span className="text-[11px] opacity-70">↗</span>
                  </a>
                )}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[#777] mono">
                <span className="inline-flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#ff4500]" />
                  Pitchup · {site.pitchup_listed ? "1-tap book" : "site directory"}
                </span>
                {site.campsites_co_uk_url && (
                  <span className="inline-flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#7c3aed]" />
                    Campsites.co.uk · search by county
                  </span>
                )}
                {site.website && (
                  <span className="inline-flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#1f5fbf]" />
                    Direct · own website
                  </span>
                )}
                {site.phone && (
                  <span className="inline-flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#1f7a35]" />
                    Direct · {site.phone}
                  </span>
                )}
              </div>
              <div className="mt-3">
                <ReportPriceButton campsiteId={id} campsiteName={site.name} />
              </div>
              {!site.website && (
                <ReportWebsiteButton campsiteId={id} campsiteName={site.name} />
              )}
              </>
            )}

            <div className="h-[320px] border hairline relative group overflow-hidden" data-testid="detail-map-wrap" style={{ isolation: "isolate" }}>
              {!canSeePremium ? (
                <LockedCampsiteOverlay label="Map">
                  <div className="h-[320px] w-full bg-gradient-to-br from-[#e6efe0] via-[#d8e5d0] to-[#c8dbb8]" aria-hidden="true" />
                </LockedCampsiteOverlay>
              ) : typeof site.lat === "number" && typeof site.lng === "number" ? (
              <MapContainer
                center={[site.lat, site.lng]} zoom={10}
                className="h-full w-full"
                scrollWheelZoom={false}
                dragging={mapInteractive}
                touchZoom={mapInteractive}
                doubleClickZoom={mapInteractive}
                tap={mapInteractive}
                zoomControl={mapInteractive}
                style={{ pointerEvents: mapInteractive ? "auto" : "none", zIndex: 0 }}
              >
                <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />
                <Marker position={[site.lat, site.lng]} icon={markerIcon} />
                {/* iter122 — Fish pins for nearby OSM fisheries (top 5 within 10 km). */}
                {mapFisheries.map((f) => (
                  typeof f.lat === "number" && typeof f.lng === "number" ? (
                    <Marker
                      key={f.osm_id || `${f.lat},${f.lng}`}
                      position={[f.lat, f.lng]}
                      icon={fisheryIcon}
                      data-testid={`detail-map-fishery-${f.osm_id}`}
                    >
                      <Tooltip direction="top" offset={[0, -8]} opacity={0.95}>
                        <div style={{ fontSize: 11, lineHeight: 1.3 }}>
                          <strong>🐟 {f.name}</strong>
                          <br />
                          <span style={{ color: "#555" }}>
                            {f.distance_from_campsite_km != null
                              ? `${f.distance_from_campsite_km} km · `
                              : ""}
                            fishery
                          </span>
                        </div>
                      </Tooltip>
                    </Marker>
                  ) : null
                ))}
                <DetailMapAttributionFix />
              </MapContainer>
              ) : (
                <div className="h-full w-full flex items-center justify-center text-xs mono text-[#888] bg-[#fafafa]">
                  Location not yet mapped
                </div>
              )}
              {!mapInteractive && (
                <button
                  type="button"
                  onClick={() => setMapInteractive(true)}
                  className="absolute inset-0 z-10 flex items-center justify-center bg-black/0 hover:bg-black/10 transition-colors"
                  data-testid="detail-map-activate"
                  aria-label="Activate map interaction"
                >
                  <span className="px-4 py-2 bg-white/95 border hairline-strong text-xs uppercase tracking-wider font-bold mono shadow-[0_2px_0_0_#111]">
                    Click to interact with map
                  </span>
                </button>
              )}
            </div>
          </aside>
        </div>
      </div>

      {/* Sticky mobile book bar — always-visible affiliate CTA while scrolling.
          Signed-out visitors see a "Sign up free" CTA instead of the price +
          Pitchup button, to match the sidebar lock behaviour. */}
      <div
        className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t hairline px-4 py-3 flex items-center gap-3 shadow-[0_-4px_16px_rgba(0,0,0,0.08)]"
        data-testid="detail-sticky-book"
      >
        {canSeePremium ? (
          <>
            <div className="min-w-0 flex-1">
              <div className="eyebrow text-[10px]">From</div>
              <div className="font-display text-lg leading-none mono">
                {typeof site.price_per_night === "number" ? (
                  <>£{site.price_per_night.toFixed(2)}<span className="text-xs text-[#888]"> /nt</span></>
                ) : (
                  <span className="text-sm text-[#888]">{t("campsite.seeOnPitchup")}</span>
                )}
              </div>
            </div>
            <BookOnPitchup
              campsiteId={id}
              pitchupListed={site.pitchup_listed}
              pitchupUrl={site.pitchup_url || null}
              pitchupUrlOk={site.pitchup_url_ok}
              campsiteName={site.name}
              campsiteRegion={site.region}
              websiteUrl={site.website || null}
              phone={site.phone || null}
              googleMapsUrl={site.google_maps_url || null}
              tripStart={tripStart || null}
              tripEnd={tripEnd || null}
              variant="primary"
              size="md"
              label="Find on Pitchup"
              testId="detail-sticky-book-btn"
            />
          </>
        ) : (
          <>
            <div className="min-w-0 flex-1">
              <div className="eyebrow text-[10px] text-[#7a2000] inline-flex items-center gap-1">
                <Lock className="w-3 h-3" /> Members only
              </div>
              <div className="font-display text-sm leading-tight text-[#333] mt-0.5">
                Sign up free to see price & book
              </div>
            </div>
            <a
              href={`/register?next=${encodeURIComponent(typeof window !== "undefined" ? window.location.pathname : `/campsites/${id}`)}`}
              className="shrink-0 inline-flex items-center justify-center gap-1.5 bg-[#ff4500] text-white font-black text-xs uppercase tracking-widest px-4 py-3 shadow-[0_3px_0_0_#111] hover:translate-y-[1px] hover:shadow-[0_2px_0_0_#111] transition-transform"
              data-testid="detail-sticky-signup-btn"
            >
              Sign up free
            </a>
          </>
        )}
      </div>
      {/* spacer so sticky bar doesn't cover content on mobile */}
      <div className="lg:hidden h-20" aria-hidden="true" />

      {/* iter176 — Conversion prompts for anonymous campers. All three no-op
          when the user is signed in. Big lever: 99% of daily traffic hits a
          campsite detail page directly (never touches Landing where the
          original signup modal lived) so these are the actual funnel now. */}
      <SignupNudgeModal />
      <SaveCampsiteBar campsiteId={id} campsiteName={site?.name} />
      <SaveThisChip campsiteName={site?.name} />
      <SignupPromptModal
        open={signupPromptOpen}
        onClose={() => setSignupPromptOpen(false)}
        title={`Save ${site?.name || "this campsite"} — free account, 10 seconds`}
        body="Signed-in campers save unlimited favourites, add trip dates for weather + price alerts, and swap tips with the community. No card, no catches."
        nextPath={`/campsites/${id}`}
      />
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between border-t hairline pt-2">
    <span className="eyebrow">{k}</span>
    <span className="mono">{v}</span>
  </div>
);
